import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useFlow } from '../../contexts/FlowContext';
import { WebFlowLayout } from '../WebFlowLayout';
import { FlowPrimaryButton } from '../components/FlowPrimaryButton';
import { StudioFlowColors, StudioFlowRadii } from '../studioFlowTheme';
import { SessionAPI } from '../../utils/sessionApi';

const CHATBOOKS_HOME_URL = 'https://chatbooks.com';

const CONFIRMATION_STEPS = [
  {
    icon: PhotoLibraryOutlinedIcon,
    title: 'Photos received',
    body: 'Your book is being assigned to your Chatbooks Studio designer.',
  },
  {
    icon: ChatBubbleOutlineOutlinedIcon,
    title: 'We\u2019ll text you a link to your draft',
    body: 'Expect it in 1-2 business days.',
  },
  {
    icon: VisibilityOutlinedIcon,
    title: 'Edit and review',
    body: 'Review your draft, make any edits, then approve to print.',
  },
  {
    icon: MenuBookOutlinedIcon,
    title: 'Print your book and enjoy',
    body: undefined,
  },
] as const;

export const ConfirmationScreen: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { state } = useFlow();

  useEffect(() => {
    if (!state.sessionId || !state.sessionKey) {
      return;
    }

    SessionAPI.updateSessionStatus(state.sessionId, state.sessionKey, 'photos_uploaded');
  }, [state.sessionId, state.sessionKey]);

  const handleCta = () => {
    window.location.assign(CHATBOOKS_HOME_URL);
  };

  const ctaButton = (
    <FlowPrimaryButton onClick={handleCta}>
      Back to Chatbooks
    </FlowPrimaryButton>
  );

  return (
    <WebFlowLayout maxWidth="md">
      <Box
        sx={{
          mx: 'auto',
          maxWidth: 787,
          bgcolor: StudioFlowColors.background,
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          mb: { xs: 12, md: 0 },
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontFamily: StudioFlowColors.displayFontFamily,
            color: StudioFlowColors.title,
            fontWeight: 400,
            fontSize: { xs: 34, md: 44 },
            lineHeight: 1,
            textAlign: 'center',
            mb: { xs: 4, md: 5 },
          }}
        >
          Thanks for your order!
        </Typography>

        <Box
          sx={{
            position: 'relative',
            mx: 'auto',
            maxWidth: 354,
            mb: { xs: 4, md: 5 },
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: 31,
              top: 64,
              bottom: 32,
              width: 0,
              borderLeft: `2px solid ${StudioFlowColors.selectionStroke}`,
            }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {CONFIRMATION_STEPS.map((step) => {
              const Icon = step.icon;

              return (
                <Box
                  key={step.title}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: step.body ? 'flex-start' : 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 62,
                      height: 64,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: StudioFlowColors.surface,
                      border: `1px solid ${StudioFlowColors.selectionStroke}`,
                      borderRadius: `${StudioFlowRadii.input}px`,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 24,
                        color: StudioFlowColors.title,
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: StudioFlowColors.bodyFontFamily,
                        fontWeight: 700,
                        fontSize: 17,
                        lineHeight: 1.3,
                        letterSpacing: '-0.17px',
                        color: StudioFlowColors.title,
                        mb: step.body ? 0.5 : 0,
                      }}
                    >
                      {step.title}
                    </Typography>
                    {step.body && (
                      <Typography
                        sx={{
                          fontFamily: StudioFlowColors.bodyFontFamily,
                          fontWeight: 450,
                          fontSize: 15,
                          lineHeight: 1.5,
                          letterSpacing: '-0.15px',
                          color: StudioFlowColors.subtitle,
                        }}
                      >
                        {step.body}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {isDesktop && <Box sx={{ maxWidth: 354, mx: 'auto' }}>{ctaButton}</Box>}
      </Box>
      {!isDesktop && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            bgcolor: StudioFlowColors.background,
            px: 3,
            pt: 2,
            pb: 'calc(16px + env(safe-area-inset-bottom))',
          }}
        >
          {ctaButton}
        </Box>
      )}
    </WebFlowLayout>
  );
};
